// ============================================================================
// API SERVICE LAYER  (public storefront data)
// ----------------------------------------------------------------------------
// Uses the Laravel REST API when REACT_APP_USE_LIVE_API=true; otherwise falls
// back to the in-memory mock so the SPA continues to work offline.
// Site settings (getSiteConfig) are the one exception — admin-managed only,
// no fallback. See note on getSiteConfig() below.
// ============================================================================

import {
  testimonials,
  videoTestimonials,
  searchSuggestions,
  shapes,
  heroSlides,
} from '../data/mock';
import { http, callOrMock, API_BASE } from './http';


// ---------------------------------------------------------------------------
// Response mappers  (Laravel -> the frontend's flat product shape)
// ---------------------------------------------------------------------------

const asAbs = (url) => {
  if (!url || url.startsWith('http')) return url;
  const base = API_BASE.replace(/\/api$/, '');
  return `${base}/storage/${url}`;
};

function productFromApi(p) {
  if (!p) return null;
  const original = Number(p.original_price ?? p.price);
  const price    = Number(p.price);
  return {
    id: String(p.id),
    slug: p.slug,
    category: p.category?.slug || p.category_slug || p.category,
    title: p.name || p.title,
    price,
    originalPrice: original,
    save: Math.max(0, original - price),
    image: asAbs(p.primary_image) || (p.gallery && asAbs(p.gallery[0])),
    gallery: Array.isArray(p.gallery) ? p.gallery.map(asAbs) : [],
    videos: Array.isArray(p.videos) ? p.videos.map(asAbs) : [],
    sizes: Array.isArray(p.sizes) ? p.sizes : [],
    reviews: p.reviews_count ?? 0,
    rating: p.rating ?? 5,
    stock: Number(p.stock ?? 0),
    stock_status: p.stock_status || (p.stock > 3 ? 'in_stock' : p.stock > 0 ? 'low_stock' : 'out_of_stock'),
    description: p.description || p.short_description || '',
    is_active: p.is_active !== false,
  };
}

function bannerFromApi(b) {
  return {
    id: b.id,
    title: b.title,
    image: asAbs(b.image_url || b.image),
    link: b.link || b.url || '',
    sortOrder: b.sort_order ?? 0,
  };
}

// ---------------------------------------------------------------------------
// Site
// ---------------------------------------------------------------------------
export async function getSiteConfig() {
  // No mock fallback — site settings are admin-managed only. If this call
  // fails, the caller (SiteConfigContext) shows a loading/error state
  // instead of silently rendering stale seed data.
  return http.get('/site/config');
}

// ---------------------------------------------------------------------------
// Banners / Hero slides — same data, two names for backward compatibility.
// ---------------------------------------------------------------------------
export async function getBanners() {
  return callOrMock(
    async () => {
      const res = await http.get('/banners');
      const raw = Array.isArray(res) ? res : (res.data || []);
      return raw.map(bannerFromApi);
    },
    () => heroSlides,
  );
}

export async function getHeroSlides() {
  return getBanners();
}

export async function getNavCategories() {
  const cats = await getCategories();
  return cats.map(c => ({ id: c.id, name: c.name, apiCategory: c.id }));
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------
export async function getCategories() {
  const list = await http.get('/categories');
  return (list || []).map(c => ({ id: c.slug, name: c.name, image: asAbs(c.image), count: c.products_count }));
}

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------
export async function getProducts({ category = null, limit = null, sort = 'featured' } = {}) {
  const q = new URLSearchParams();
  if (category && category !== 'all') q.set('category', category);
  if (limit) q.set('limit', String(limit));
  if (sort)  q.set('sort', sort);
  const res = await http.get(`/products?${q.toString()}`);
  const raw = Array.isArray(res) ? res : (res.data || []);
  return raw.map(productFromApi);
}

export async function getProductById(id) {
  return productFromApi(await http.get(`/products/${id}`));
}

export async function getBestSellers(limit = 4) {
  return getProducts({ limit, sort: 'featured' });
}

export async function getRelated(product, limit = 6) {
  const res = await http.get(`/products/${product.slug || product.id}/related`);
  return (res || []).map(productFromApi).slice(0, limit);
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------
export async function searchProducts(query) {
  if (!query) return { suggestions: searchSuggestions.slice(0, 4), products: [] };
  const res = await http.get(`/search?q=${encodeURIComponent(query)}`);
  return {
    suggestions: res.suggestions || [],
    products:    (res.products || []).map(productFromApi),
  };
}

// ---------------------------------------------------------------------------
// Testimonials / Shapes
// ---------------------------------------------------------------------------
export async function getTestimonials() {
  return callOrMock(
    async () => http.get('/testimonials'),
    () => testimonials,
  );
}

export async function getVideoTestimonials() {
  return callOrMock(
    async () => {
      const res = await http.get('/testimonials/video');
      return (res || []).map(v => ({ ...v, thumb: asAbs(v.thumb) }));
    },
    () => videoTestimonials,
  );
}

export async function getShapes() {
  return callOrMock(
    async () => {
      const res = await http.get('/shapes');
      return (res || []).map(s => ({ ...s, image: asAbs(s.image) }));
    },
    () => shapes,
  );
}

// ---------------------------------------------------------------------------
// Coupon validate (used by Checkout to preview discount)
// Returns { valid, code, discount, message } — mock provides three demo codes.
// ---------------------------------------------------------------------------
const DEMO_COUPONS = {
  WELCOME10:  { type: 'percent', value: 10, min: 0 },
  FLAT500:    { type: 'flat',    value: 500, min: 5000 },
  VICTORIA25: { type: 'percent', value: 25, min: 15000 },
};

export async function validateCoupon({ code, subtotal }) {
  return callOrMock(
    async () => http.post('/coupons/validate', { code, subtotal }),
    () => {
      const key = String(code || '').toUpperCase();
      const c = DEMO_COUPONS[key];
      if (!c)                     return { valid: false, message: 'Invalid coupon code.' };
      if (subtotal < c.min)       return { valid: false, message: `Minimum order of Rs. ${c.min.toLocaleString('en-IN')} required.` };
      const disc = c.type === 'percent' ? Math.round((subtotal * c.value) / 100) : c.value;
      return { valid: true, code: key, type: c.type, value: c.value, discount: Math.min(disc, subtotal), message: 'Coupon applied.' };
    },
  );
}

export async function getPage(slug) {
  return http.get(`/pages/${slug}`);
}

export async function sendContactMessage(data) {
  return http.post('/contact', data);
}